import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  Paperclip, Mic, MicOff, Zap, Lock, ArrowRight, X, Image as ImageIcon,
  MousePointer2, Code2, Globe, ListChecks,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import PagePreloader from '@/components/PagePreloader';
import WebsitePreviewModal from '@/components/WebsitePreviewModal';
import { StartFromAnIdea } from '@/components/home/ShowcaseGallery';
import RecentAppCard from '@/components/home/RecentAppCard';
import MarketplaceShowcase from '@/components/marketplace/MarketplaceShowcase';
import { Tile, IconSquare, BTN_PRIMARY, BTN_QUIET } from '@/components/bento';
import AuthModal from '@/components/AuthModal';
import { buildSiteFiles } from '@/lib/sitePages';
import { MAX_PROMPT, promptCountTone } from '@/components/generation/PromptComposer';
import PlanFirstToggle from '@/components/generation/PlanFirstToggle';
import { usePlanFirst } from '@/lib/buildPreferences';
import {
  readImageAttachment,
  speechRecognitionAvailable,
  startDictation,
  appendTranscript,
  dictationErrorMessage,
} from '@/lib/promptInput';

const API_URL = import.meta.env.VITE_API_URL ?? '';

// ─── Prompt parked behind sign-in ─────────────────────────────────────────────
// Clicking Build while logged out used to dead-end on a toast, leaving the user
// to find the sign-in button themselves and retype their idea. The prompt is
// stashed instead, so the build can resume itself once auth completes.
//
// localStorage rather than sessionStorage because signing up finishes in a
// *different* page load: the confirmation link opens a fresh tab. The timestamp
// keeps an abandoned prompt from firing a surprise generation — and burning a
// credit — days later.
const PENDING_PROMPT_KEY = 'bl_pending_build_prompt';
const PENDING_PROMPT_TTL_MS = 30 * 60 * 1000;

function savePendingPrompt(text) {
  try {
    localStorage.setItem(PENDING_PROMPT_KEY, JSON.stringify({ prompt: text, ts: Date.now() }));
  } catch { /* private browsing may block localStorage — the gate still works */ }
}

function readPendingPrompt() {
  try {
    const raw = localStorage.getItem(PENDING_PROMPT_KEY);
    if (!raw) return '';
    const { prompt, ts } = JSON.parse(raw);
    if (!prompt || !ts || Date.now() - ts > PENDING_PROMPT_TTL_MS) {
      localStorage.removeItem(PENDING_PROMPT_KEY);
      return '';
    }
    return prompt;
  } catch { return ''; }
}

function clearPendingPrompt() {
  try { localStorage.removeItem(PENDING_PROMPT_KEY); } catch { /* ignore */ }
}

// The four tiles under the hero. Each names something the product does today —
// a tile is a promise, so nothing goes here that a plan does not deliver.
const FEATURES = [
  {
    icon: MousePointer2,
    title: 'Edit by clicking',
    body: 'Click any headline, image or video on your site and change it. No prompt needed.',
  },
  {
    icon: ListChecks,
    title: 'Plan mode',
    body: 'See and change every page before a credit is spent — then build.',
  },
  {
    icon: Globe,
    title: 'Publish in one click',
    body: 'Go live on a builderslab.net address, or on your own domain with Pro.',
  },
  {
    icon: Code2,
    title: 'Take the code',
    body: 'Export clean HTML or a ready-to-run React project with any paid plan.',
  },
];

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'yesterday';
  if (days < 30)  return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}


export default function AIBuilderSection({ initialPrompt = '' }) {
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(initialPrompt);
  const [isRecording, setIsRecording] = useState(false);
  const [placeholder, setPlaceholder] = useState('');
  const [showPreloader, setShowPreloader] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isCheckingCredits, setIsCheckingCredits] = useState(false);
  const [recentApps, setRecentApps] = useState([]);
  const [loadingApps, setLoadingApps] = useState(false);
  const [activeSite, setActiveSite] = useState(null);
  const [isPaid, setIsPaid] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  // The picked image, kept as the File (for the chip) and as the base64 payload
  // /api/generate expects. It is handed to /build in router state on launch.
  /** @type {[File|null, Function]} */
  const [attachedFile, setAttachedFile]   = useState(null);
  /** @type {[{base64: string, mimeType: string}|null, Function]} */
  const [attachedImage, setAttachedImage] = useState(null);
  const [planFirst, setPlanFirst] = usePlanFirst();
  const fileInputRef = useRef(null);
  /** @type {React.MutableRefObject<any>} */
  const recognitionRef = useRef(null);
  // A signup awaiting email confirmation must keep its parked prompt even though
  // the modal is about to be dismissed with no session behind it.
  const awaitingEmailConfirmRef = useRef(false);
  const resumedBuildRef = useRef(false);

  useEffect(() => {
    const placeholders = [
      "What are we building today? e.g. 'A luxury real estate platform with high-end listings...'",
      "A sleek fintech banking app with dark mode...",
      "A vibrant food blog with recipe cards...",
      "A minimalist Web3 developer portfolio..."
    ];
    let currentIndex = 0;
    let isDeleting = false;
    let currentText = '';
    let timeout;
    
    // Quick pause before starting
    let delay = 500; 

    const type = () => {
      const fullText = placeholders[currentIndex];

      if (isDeleting) {
        currentText = fullText.substring(0, currentText.length - 1);
        delay = 20; // Fast delete
      } else {
        currentText = fullText.substring(0, currentText.length + 1);
        delay = 40; // Typing speed
      }

      setPlaceholder(currentText);

      // Status checks
      if (!isDeleting && currentText === fullText) {
        delay = 2500; // Pause at the end before deleting
        isDeleting = true;
      } else if (isDeleting && currentText === '') {
        isDeleting = false;
        currentIndex = (currentIndex + 1) % placeholders.length;
        delay = 500; // Pause before typing new text
      }

      timeout = setTimeout(type, delay);
    };

    timeout = setTimeout(type, delay);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (!user || !session?.access_token) return;
    setLoadingApps(true);
    fetch(`${API_URL}/api/websites`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then(r => (r.ok ? r.json() : { websites: [] }))
      .then(({ websites }) => { setRecentApps((websites || []).slice(0, 6)); })
      .catch(() => { setRecentApps([]); })
      .finally(() => setLoadingApps(false));
  }, [user, session]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('subscription_tier')
      .eq('id', user.id)
      .single()
      .then(({ data }) => { setIsPaid((data?.subscription_tier ?? 'free') !== 'free'); })
      .catch(() => {});
  }, [user]);

  const handleOpenApp = useCallback((app) => {
    // The list from /api/websites already carries full content, so pass the
    // record along and the workspace renders without a second fetch. The id in
    // the URL is what makes the link shareable and refresh-proof.
    if (app) navigate(`/build/${app.id}`, { state: { website: app } });
    else toast.error('Could not load app.');
  }, [navigate]);

  const handleDeleteApp = useCallback(async (id) => {
    if (!session?.access_token) return;
    try {
      await fetch(`${API_URL}/api/websites/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setRecentApps(prev => prev.filter(w => w.id !== id));
    } catch { toast.error('Failed to delete website.'); }
  }, [session]);

  const handlePatchApp = useCallback(async (id, updates) => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${API_URL}/api/websites/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(updates),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Failed to update.'); }
      const { website } = await res.json();
      setRecentApps(prev => prev.map(w => w.id === id ? { ...w, ...website } : w));
    } catch (err) { toast.error(err.message || 'Failed to update website.'); }
  }, [session]);

  const handleRenameApp       = useCallback((id, title) => handlePatchApp(id, { title }), [handlePatchApp]);
  const handleTogglePublicApp = useCallback((site) => handlePatchApp(site.id, { is_public: !site.is_public }), [handlePatchApp]);

  const handleCopyLinkApp = useCallback((site) => {
    const url = `${window.location.origin}/preview/${site.id}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Share link copied!'));
  }, []);

  const handleExportZipApp = useCallback(async (site) => {
    if (!isPaid) { toast.error('Upgrade to a paid plan to export website code.'); return; }
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      // Every generated page plus the shared assets — see lib/sitePages.js.
      buildSiteFiles(site).forEach(f => zip.file(f.name, f.content));
      const blob = await zip.generateAsync({ type: 'blob' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${(site.title || 'website').replace(/\s+/g, '-')}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export ZIP.'); }
  }, [isPaid]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input straight away, so re-picking the same file still fires.
    e.target.value = '';
    if (!file) return;

    try {
      const { file: picked, image } = await readImageAttachment(file);
      setAttachedFile(picked);
      setAttachedImage(image);
      toast.success(`${picked.name} attached`);
    } catch (err) {
      setAttachedFile(null);
      setAttachedImage(null);
      toast.error(err.message);
    }
  };

  const clearAttachment = () => {
    setAttachedFile(null);
    setAttachedImage(null);
  };

  const handleVoiceRecord = () => {
    if (isRecording) {
      recognitionRef.current?.stop();
      return;
    }
    if (!speechRecognitionAvailable()) {
      toast.error('Voice input is not supported in this browser.');
      return;
    }

    recognitionRef.current = startDictation({
      onTranscript: (transcript) =>
        setPrompt(prev => appendTranscript(prev, transcript).slice(0, MAX_PROMPT)),
      onEnd: () => setIsRecording(false),
      onError: (event) => {
        setIsRecording(false);
        const message = dictationErrorMessage(event);
        if (message) toast.error(message);
      },
    });
    setIsRecording(true);
  };

  // A recogniser left running after the section unmounts keeps the mic light on.
  useEffect(() => () => {
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
  }, []);

  // Checks credits and drops into the generation view. Split out of the click
  // handler so a build parked behind sign-in resumes down exactly the same path.
  const startBuild = useCallback(async (text) => {
    if (!user || !text) return;

    setIsCheckingCredits(true);
    try {
      const { data } = await supabase
        .from('profiles')
        .select('credits')
        .eq('id', user.id)
        .single();

      if (data && data.credits !== null && data.credits <= 0) {
        setShowUpgradeModal(true);
        return;
      }
    } finally {
      setIsCheckingCredits(false);
    }

    // Hand the run to /build. The prompt and the image travel in router state
    // rather than a query string — a 4 MB base64 image has no business in a URL,
    // and neither belongs in the address bar.
    const image = attachedImage;
    setAttachedFile(null);
    setAttachedImage(null);
    setShowPreloader(true);
    setTimeout(() => {
      setShowPreloader(false);
      navigate('/build', { state: { prompt: text, image, planFirst } });
    }, 3000);
  }, [user, attachedImage, planFirst, navigate]);

  const handleLaunchBuilder = () => {
    const text = prompt.trim();
    if (!text) {
      toast.warning('Please enter a prompt first.');
      return;
    }

    if (!user) {
      savePendingPrompt(text);
      awaitingEmailConfirmRef.current = false;
      setIsAuthModalOpen(true);
      return;
    }

    startBuild(text);
  };

  // Resume a parked build. Covers both routes back from auth: the modal closing
  // after a sign-in, and a cold page load after the user confirmed their email.
  useEffect(() => {
    if (!user || resumedBuildRef.current) return;
    if (showPreloader) return;

    const pending = readPendingPrompt();
    if (!pending) return;

    resumedBuildRef.current = true;
    clearPendingPrompt();
    setIsAuthModalOpen(false);
    setPrompt(pending);
    toast.success('You’re in — building your site now.');
    startBuild(pending);
  }, [user, showPreloader, startBuild]);

  const handleAuthModalChange = useCallback((open) => {
    setIsAuthModalOpen(open);
    if (open) return;
    // Dismissed. Drop the parked prompt unless auth is still on its way, so it
    // can't fire an unexpected build the next time this page mounts. A successful
    // sign-in also closes the modal, and `user` may not have propagated yet in
    // this tick — so ask Supabase for the live session rather than trusting state.
    if (awaitingEmailConfirmRef.current) return;
    supabase.auth.getSession()
      .then(({ data: { session } }) => { if (!session) clearPendingPrompt(); })
      .catch(() => {});
  }, []);

  return (
    <>
    <PagePreloader isVisible={showPreloader} />

    {/* ── Sign-in gate: the build resumes on its own once auth completes ────── */}
    <AuthModal
      isOpen={isAuthModalOpen}
      onClose={handleAuthModalChange}
      description="Sign in or create an account to start building — we've saved your prompt and will pick up right where you left off."
      onAwaitingEmailConfirm={() => { awaitingEmailConfirmRef.current = true; }}
    />

    <AnimatePresence>
      {activeSite && (
        <WebsitePreviewModal
          site={activeSite}
          isPaid={isPaid}
          onClose={() => setActiveSite(null)}
          onUpgrade={() => { setActiveSite(null); navigate('/Pricing'); }}
        />
      )}
    </AnimatePresence>

    {/* ── Out-of-credits upgrade modal ─────────────────────────────── */}
    <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
      <DialogContent className="sm:max-w-md bg-zinc-900 border-zinc-800 text-white rounded-3xl sm:rounded-3xl">
        <div className="p-2 text-center space-y-5">
          <div className="w-16 h-16 bg-orange-500/15 rounded-2xl flex items-center justify-center mx-auto">
            <Lock className="w-7 h-7 text-orange-400" />
          </div>
          <div>
            <DialogTitle className="text-2xl font-extrabold tracking-tight text-white mb-2">
              You're out of credits
            </DialogTitle>
            {/* This said "all 5 free generations" long after the free plan
                stopped being five, and it shows to paid plans too. */}
            <DialogDescription className="text-zinc-400 text-sm max-w-xs mx-auto">
              Every credit on your plan has been used. Upgrade to keep building with BuildersLab AI.
            </DialogDescription>
          </div>
          <div className="space-y-3 pt-1">
            <Button
              onClick={() => { setShowUpgradeModal(false); navigate('/Pricing'); }}
              className={`w-full h-11 font-bold rounded-full ${BTN_PRIMARY}`}
            >
              <Zap className="w-4 h-4 mr-2" />
              View Pricing Plans
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowUpgradeModal(false)}
              className="w-full h-9 rounded-full text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 text-sm"
            >
              Maybe later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <section className="relative bg-zinc-950 pt-24 pb-6 sm:pt-28">
      <div className="w-full min-w-0 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-12">

          {/* ── The prompt: the product's front door, the whole first row ──── */}
          <Tile className="md:col-span-2 lg:col-span-12 px-5 py-10 sm:px-10 sm:py-14 lg:py-16 flex flex-col items-center text-center min-w-0">
            <h1 className="text-[2.5rem] sm:text-5xl lg:text-[3.5rem] leading-[1.04] font-extrabold tracking-[-0.045em] text-white">
              What do you <br className="hidden sm:block" /> wanna build?
            </h1>
            <p className="mt-4 max-w-xl text-base sm:text-lg leading-relaxed text-zinc-400">
              Describe what you want to build in plain English.
            </p>

            {/* Prompt Container */}
            <div className="mt-8 w-full max-w-3xl text-left rounded-2xl border border-zinc-800 bg-zinc-950 p-2 sm:p-3 focus-within:border-zinc-700 transition-colors">

              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value.slice(0, MAX_PROMPT))}
                placeholder={placeholder}
                aria-label="Describe your website"
                className="min-h-[96px] bg-transparent border-0 shadow-none text-white placeholder:text-zinc-500 text-base md:text-lg resize-none focus-visible:ring-0 focus-visible:ring-offset-0 px-3 py-2"
              />
              {prompt.length > 0 && (
                <div className="px-3 pb-1 flex justify-end">
                  <span className={`text-xs tabular-nums font-medium transition-colors ${promptCountTone(prompt.length)}`}>
                    {prompt.length} / {MAX_PROMPT}
                  </span>
                </div>
              )}

              {/* Attached image — shown only once it is genuinely readable and
                  staged for the request, so the chip never over-promises. */}
              {attachedFile && (
                <div className="px-3 pb-1">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-800 text-xs text-zinc-300">
                    <ImageIcon className="w-3 h-3 text-blue-400" />
                    <span className="max-w-[180px] truncate">{attachedFile.name}</span>
                    <span className="text-blue-400 text-[10px]">· vision</span>
                    <button
                      type="button"
                      onClick={clearAttachment}
                      aria-label={`Remove ${attachedFile.name}`}
                      className="ml-0.5 text-zinc-500 hover:text-white transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between gap-2 px-1 pt-2 mt-2 border-t border-zinc-800">
                <div className="flex items-center gap-1.5">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label={attachedFile ? 'Change Image' : 'Upload Image'}
                    className={`${BTN_QUIET} rounded-full h-9 px-3.5 text-sm`}
                  >
                    <Paperclip className="w-4 h-4 sm:mr-1.5" />
                    <span className="hidden sm:inline">{attachedFile ? 'Change Image' : 'Upload Image'}</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleVoiceRecord}
                    aria-pressed={isRecording}
                    aria-label={isRecording ? 'Listening…' : 'Record'}
                    className={`${BTN_QUIET} rounded-full h-9 px-3.5 text-sm ${isRecording ? 'bg-red-500/20 text-red-500 animate-pulse' : ''}`}
                  >
                    {isRecording ? <MicOff className="w-4 h-4 sm:mr-1.5" /> : <Mic className="w-4 h-4 sm:mr-1.5" />}
                    <span className="hidden sm:inline">{isRecording ? 'Listening…' : 'Record'}</span>
                  </Button>
                </div>

                <div className="hidden sm:flex items-center gap-3">
                  <PlanFirstToggle checked={planFirst} onCheckedChange={setPlanFirst} />
                  <Button
                    size="lg"
                    onClick={handleLaunchBuilder}
                    disabled={!prompt.trim() || isCheckingCredits}
                    className={`${BTN_PRIMARY} font-bold rounded-full h-10 px-5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isCheckingCredits ? 'Checking…' : 'Build'}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {/* Mobile Generate Button. The switch sits above it here: the row
                  beside Upload Image and Record has no room for it on a phone. */}
              <div className="sm:hidden mt-3 px-1 pb-1">
                <PlanFirstToggle checked={planFirst} onCheckedChange={setPlanFirst} className="justify-end mb-2.5" />
                <Button
                  size="lg"
                  onClick={handleLaunchBuilder}
                  disabled={!prompt.trim() || isCheckingCredits}
                  className={`${BTN_PRIMARY} w-full font-bold rounded-full h-12 disabled:opacity-50`}
                >
                  {isCheckingCredits ? 'Checking…' : 'Build'}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Tile>

          {/* ── Start from an idea ───────────────────────────────────────── */}
          {/* Straight after the prompt it feeds: a card's prompt lands in the
              box above, so the two belong next to each other. */}
          <StartFromAnIdea
            className="md:col-span-2 lg:col-span-12"
            onUseIdea={(idea) => {
              setPrompt(idea);
              window.scrollTo({ top: 0, behavior: 'smooth' });
              toast.success('Prompt loaded — hit Build when ready!');
            }}
          />

          {/* ── What you get ─────────────────────────────────────────────── */}
          {FEATURES.map(({ icon, title, body }) => (
            <Tile key={title} className="lg:col-span-3 sm:p-7 p-6 flex flex-col gap-6 lg:gap-8 lg:min-h-[200px]">
              <IconSquare icon={icon} />
              <div className="mt-auto">
                <h2 className="text-xl font-extrabold tracking-tight text-white">{title}</h2>
                <p className="mt-2 text-[15px] leading-relaxed text-zinc-400">{body}</p>
              </div>
            </Tile>
          ))}

          {/* ── The marketplace: sites built here, for sale. Shows nothing
              until something is listed. ─────────────────────────────────── */}
          <MarketplaceShowcase className="md:col-span-2 lg:col-span-12" limit={6} />

          {/* ── Recent Apps Section ─────────────────────────────────────── */}
          {user && (loadingApps || recentApps.length > 0) && (
            <Tile className="md:col-span-2 lg:col-span-12 p-5 sm:p-8 text-left">
              {/* Heading + View all */}
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-white font-extrabold tracking-tight text-2xl">Recent apps</h2>
                <button
                  onClick={() => navigate('/Dashboard')}
                  className="flex items-center gap-1 text-sm font-semibold text-zinc-400 hover:text-white transition-colors"
                >
                  View all <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Recent apps grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <AnimatePresence>
                  {loadingApps
                    ? Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="rounded-2xl bg-zinc-950 border border-zinc-800 overflow-hidden animate-pulse">
                          <div className="h-44 bg-zinc-800" />
                          <div className="p-4 space-y-2">
                            <div className="h-4 bg-zinc-800 rounded w-3/4" />
                            <div className="h-3 bg-zinc-800 rounded w-1/2" />
                          </div>
                        </div>
                      ))
                    : recentApps.map(app => (
                        <RecentAppCard
                          key={app.id}
                          site={app}
                          isPaid={isPaid}
                          onDelete={handleDeleteApp}
                          onPreview={setActiveSite}
                          onGetCode={setActiveSite}
                          onEdit={handleOpenApp}
                          onRename={handleRenameApp}
                          onTogglePublic={handleTogglePublicApp}
                          onCopyLink={handleCopyLinkApp}
                          onExportZip={handleExportZipApp}
                        />
                      ))
                  }
                </AnimatePresence>
              </div>
            </Tile>
          )}
        </div>
      </div>
    </section>
    </>
  );
}
